import { createTheme, darken, lighten } from '@mui/material/styles';
import themeLiveDefault from '@shared/schemas/conferenceTemplates/themeLiveDefault.json';
import { lightOrDark } from '@/lib/utils/detect';
import themeModerator from './theme_moderator';

// Defined using
// https://material.io/resources/color/#!/?view.left=0&view.right=0&primary.color=FFF3E0&secondary.color=FBC02D

/*
"common":{
  "black":"#000","white":"#fff"},
  "background":{
    "paper":"#fff","default":"#fafafa"},
    "primary":{"light":"#7986cb","main":"#3f51b5","dark":"#303f9f","contrastText":"#fff"},
    "secondary":{"light":"#ff4081","main":"#f50057","dark":"#c51162","contrastText":"#fff"},
    "error":{"light":"#e57373","main":"#f44336","dark":"#d32f2f","contrastText":"#fff"},
    "text":{"primary":"rgba(0, 0, 0, 0.87)","secondary":"rgba(0, 0, 0, 0.54)",
      "disabled":"rgba(0, 0, 0, 0.38)",
      "hint":"rgba(0, 0, 0, 0.38)"}

      }}
*/

let themeMap = window.THEME_LIVE;

const fontIdToCSS = (fontId) => {
  return `"${themeMap.typography.fontIds[fontId].name}", Helvetica, Arial, sans-serif`;
};

const mapVariantsToFonts = (variants) => {
  return Object.entries(variants).reduce((obj, [variant, data]) => {
    // Fallback to default if a variant points to a font that doesnt exist
    if (
      Object.prototype.hasOwnProperty.call(
        themeMap.typography.fontIds,
        data.font
      )
    ) {
      obj[variant] = {
        fontFamily: fontIdToCSS(data.font),
        fontWeight: parseInt(data.fontWeight),
      };
    } else {
      obj[variant] = {
        fontFamily: fontIdToCSS(themeMap.typography.default.id),
        fontWeight: parseInt(themeMap.typography.default.regular),
      };
    }
    return obj;
  }, {});
};

const mapToVariants = (variantPrio) => {
  for (const variant of variantPrio) {
    if (themeMap.typography.variantMap[variant]) {
      return themeMap.typography.variantMap[variant];
    }
  }
  // If no variant is set, return nothing (which uses default theme values)
  return {};
};
// Make sure fonts map
try {
  // First add default fonts
  themeMap.typography.fonts = [
    ...themeMap.typography.fonts,
    ...themeLiveDefault.typography.fonts,
  ];
  // Here we'll put the mapped fonts ("tags" to fonts list)
  themeMap.typography.fontIds = themeMap.typography.fonts.reduce((ids, f) => {
    ids[f.id] = f;
    return ids;
  }, {});

  // Map all variant fontIds to fontname (and make sure they exist). "heading", "text" (could be more in the future)
  themeMap.typography.variantMap = mapVariantsToFonts(
    themeMap.typography.variants
  );
  // Add a "default" variant
  themeMap.typography.variantMap.default = {
    fontFamily: fontIdToCSS(themeMap.typography.default.font),
    fontWeight: parseInt(themeMap.typography.default.regular),
  };
  themeMap.typography.variantMap.default_bold = {
    fontFamily: fontIdToCSS(themeMap.typography.default.font),
    fontWeight: parseInt(themeMap.typography.default.bold),
  };

  // themeMap.typography.default.fontName = themeMap.typography.fontIds[themeMap.themeMap.typography.default.font];
} catch (e) {
  console.error('theme pallette mismatch', e);
}

const setCSSVar = (name, value) => {
  try {
    window.CSS.registerProperty({
      name: name,
      syntax: '<color>',
      inherits: false,
      initialValue: value,
    });
  } catch (e) {
    console.error('CSS registerProperty error', e);
  }
};

let theme;
try {
  // Detect if light or dark
  const theme_type = lightOrDark(themeMap.palette.background);

  const shadeDirection = {
    up: theme_type === 'dark' ? darken : lighten,
    down: theme_type === 'dark' ? lighten : darken,
  };
  theme = createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 820,
        lg: 1280,
        xl: 1920,
      },
    },
    components: {
      // Style sheet name ⚛️
      MuiLink: {
        // Name of the rule
        styleOverrides: {
          root: {
            fontWeight: parseInt(themeMap.typography.default.bold),
          },
        },
      },
      MuiBadge: {
        styleOverrides: {
          badge: {
            fontWeight: parseInt(themeMap.typography.default.bold),
          },
        },
      },
      MuiPaper: {
        // Name of the rule
        styleOverrides: {
          root: {
            backgroundColor: 'inherit',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            '@media (min-width:600px)': {
              minWidth: 110,
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontSize: '0.95rem',
          },
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            fontSize: 16,
            '&.MuiInputBase-multiline': {
              padding: 10,
            },
          },
          input: {
            '&:not(.MuiInputBase-inputMultiline)': {
              boxSizing: 'content-box',
              height: 23,
              padding: 10,
              fontSize: 16,
            },
          },
          inputMultiline: {
            padding: 0,
            fontSize: 16,
          },
          inputAdornedStart: {
            paddingLeft: 0,
          },
        },
      },

      MuiSelect: {
        styleOverrides: {
          select: {
            '&.MuiOutlinedInput-input': {
              boxSizing: 'content-box',
              minHeight: 'unset',
              height: '23px',
              paddingTop: '10px',
              paddingBottom: '10px',
              paddingLeft: '10px',
              paddingRight: '32px',
              fontSize: 16,
            },
          },
        },
      },

      MuiInputLabel: {
        styleOverrides: {
          outlined: {
            '&:not(.MuiInputLabel-shrink)': {
              top: 0,
              transform: 'translate(14px, 10px) scale(1)',
            },
            '&.MuiInputLabel-shrink': {
              top: 0,
              transform: 'translate(14px, -9px) scale(0.75)',
            },
          },
        },
      },

      MuiAutocomplete: {
        styleOverrides: {
          inputRoot: {
            '&.MuiOutlinedInput-root': {
              padding: '10px',
              paddingRight: '39px',
            },
          },
          input: {
            '&.MuiOutlinedInput-input': {
              padding: 0,
            },
          },
        },
      },

      MuiInputAdornment: {
        styleOverrides: {
          root: {
            '& .MuiSvgIcon-root, & .material-icons': { fontSize: 20 },
          },
          positionStart: { marginLeft: 10 },
        },
      },
    },
    typography: {
      fontWeightLight: parseInt(themeMap.typography.default.regular),
      fontWeightRegular: parseInt(themeMap.typography.default.regular),
      fontWeightMedium: parseInt(themeMap.typography.default.bold),
      fontWeightBold: parseInt(themeMap.typography.default.bold),
      fontFamily: fontIdToCSS(themeMap.typography.default.font),
      h1: {
        fontSize: '3.5rem',
        lineHeight: 1,
        ...mapToVariants(['h1', 'heading']),
      },
      h2: {
        fontSize: '3rem',
        lineHeight: 1,
        ...mapToVariants(['h2', 'heading']),
      },
      h3: {
        fontSize: '2.5rem',
        lineHeight: 1,
        ...mapToVariants(['h3', 'heading']),
      },
      h4: mapToVariants(['h4', 'heading']),
      h5: mapToVariants(['h5', 'heading']),
      h6: {
        lineHeight: 1.2,
        ...mapToVariants(['h6', 'heading']),
      },
      subtitle1: {
        fontSize: '1.1rem',
        ...mapToVariants(['subtitle1', 'default_bold']),
      }, // Uses default if not set
      subtitle2: {
        fontSize: '1.1rem',
        ...mapToVariants(['subtitle2', 'default_bold']),
      }, // Uses default if not set
      body1: {
        fontSize: '1rem',
        ...mapToVariants(['body1']),
      }, // Uses default if not set
      body2: {
        fontSize: '1.1rem',
        ...mapToVariants(['body2']),
      }, // Uses default if not set
      button: mapToVariants(['button']), // Uses default if not set
    },
    palette: {
      mode: theme_type,
      background: {
        // We're using some grades of
        // Coeo shade dark
        shadeup_10: shadeDirection.up(themeMap.palette.background, 0.6),
        default: themeMap.palette.background,
        // Paper is same as Coeo shade light
        paper: shadeDirection.down(themeMap.palette.background, 0.06),
        // Coeo shade light
        shadedown_10: shadeDirection.down(themeMap.palette.background, 0.06),
        // Coeo shade lighter
        shadedown_20: shadeDirection.down(themeMap.palette.background, 0.12),
      },
      primary: {
        main: themeMap.palette.primary, //#fbc02d
        // light: window.THEME_DATA.properties.COLORS.PRIMARY_LIGHT, //#fff263
        // dark: window.THEME_DATA.properties.COLORS.PRIMARY_DARK,
      },
      secondary: {
        main: themeMap.palette.secondary,
        // light: window.THEME_DATA.properties.COLORS.SECONDARY_LIGHT,
        // dark: window.THEME_DATA.properties.COLORS.SECONDARY_DARK,
      },
      text: {
        primary: theme_type === 'dark' ? '#ffffff' : '#000000',
        secondary:
          theme_type === 'dark'
            ? 'rgba(255, 255, 255, 0.7)'
            : 'rgba(0, 0, 0, 0.6)',
        disabled:
          theme_type === 'dark'
            ? 'rgba(255, 255, 255, 0.5)'
            : 'rgba(0, 0, 0, 0.38)',
        hint:
          theme_type === 'dark'
            ? 'rgba(255, 255, 255, 0.5)'
            : 'rgba(0, 0, 0, 0.38)',
      },
    },
    shape: {
      borderRadius: 2,
    },
    shadows: Array(25).fill('none'),
  });
  // For use in Manager Page editor (CSS Override)
  setCSSVar('--background', theme.palette.background.default);
  setCSSVar('--primary', theme.palette.primary.main);
  setCSSVar('--secondary', theme.palette.secondary.main);
  setCSSVar('--text', theme.palette.text.primary);
} catch (e) {
  console.error('Theme error, use default', e);
  theme = themeModerator;
}

export default theme;
